"use client"
import { useMemo, useState } from 'react'
import { Mail, ExternalLink } from 'lucide-react'
import { SUPPORT_EMAIL } from '../../src/lib/config'

export default function ContactClient() {
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const subjectBase = 'Support enquiry — Saffron Leo'

  function buildMailto(values: any) {
    const parts = [] as string[]
    parts.push(`Name: ${values.name}`)
    parts.push(`Email: ${values.email}`)
    if (values.orderId) parts.push(`Order ID: ${values.orderId}`)
    if (values.subject) parts.push(`Subject: ${values.subject}`)
    parts.push('')
    parts.push(values.message)
    const body = encodeURIComponent(parts.join('\n'))
    const subject = encodeURIComponent(values.subject || subjectBase)
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
  }

  const [form, setForm] = useState({ name: '', email: '', phone: '', orderId: '', subject: '', message: '', botField: '' })
  const [touched, setTouched] = useState({ name: false, email: false, phone: false, message: false })

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim())
  const validatePhone = (value: string) => {
    const raw = String(value || '').trim()
    if (!raw) return true // optional
    const compact = raw.replace(/[\s-]/g, '')
    const e164 = /^\+?[1-9]\d{7,14}$/
    const digits = raw.replace(/[^\d]/g, '')
    return e164.test(compact) || digits.length === 10
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setStatus('Sending...')
    try {
      const valid = form.name.trim() && validateEmail(form.email) && validatePhone(form.phone) && form.message.trim()
      if (!valid) {
        setTouched({ name: true, email: true, phone: true, message: true })
        setError('Please fill all required fields with valid details before sending.')
        return
      }
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          orderId: form.orderId,
          subject: form.subject || subjectBase,
          message: form.message,
          botField: form.botField || '',
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || 'send_failed')
      setSent(true)
      setStatus('✅ Message sent successfully!')
      setForm({ ...form, subject: '', orderId: '', message: '' })
    } catch (err) {
      setError('Could not send message. Please use the email link below or try again later.')
      setStatus('❌ Failed: ' + (err as any)?.message || 'Unknown error')
    }
  }

  const canSubmit = useMemo(() => form.name.trim() && validateEmail(form.email) && form.message.trim(), [form])

  return (
    <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <form onSubmit={onSubmit} className="space-y-4 border bg-white px-6 py-6 shadow-sm">
        <input type="text" name="bot-field" value={form.botField} onChange={e => setForm({ ...form, botField: (e.target as HTMLInputElement).value })} className="hidden" aria-hidden />

        {sent ? (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            Thanks for reaching out. Your message has been sent. We’ll reply within 1–2 business days.
          </div>
        ) : null}

        {error && !sent ? (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Your name <span className="text-red-600" aria-hidden>*</span></span>
            <input required value={form.name} onChange={e => setForm({ ...form, name: (e.target as HTMLInputElement).value })} onBlur={() => setTouched(t => ({ ...t, name: true }))} aria-invalid={touched.name && !form.name.trim()} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="Mohit" />
            {touched.name && !form.name.trim() && (<p className="text-xs text-red-600 mt-1">Please enter your name.</p>)}
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Email <span className="text-red-600" aria-hidden>*</span></span>
            <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: (e.target as HTMLInputElement).value })} onBlur={() => setTouched(t => ({ ...t, email: true }))} aria-invalid={touched.email && !validateEmail(form.email)} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="you@example.com" />
            {touched.email && !validateEmail(form.email) && (<p className="text-xs text-red-600 mt-1">Please enter a valid email address.</p>)}
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
              <span className="text-gray-700">Phone number <span className="text-gray-400" aria-hidden>(optional)</span></span>
            <input type="tel" inputMode="tel" name="phone" value={form.phone} onChange={e => setForm({ ...form, phone: (e.target as HTMLInputElement).value })} onBlur={() => setTouched(t => ({ ...t, phone: true }))} aria-invalid={touched.phone && !validatePhone(form.phone)} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="e.g. +91 98765 43210" />
            {touched.phone && form.phone && !validatePhone(form.phone) && (<p className="text-xs text-red-600 mt-1">Please enter a valid phone number.</p>)}
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Order ID (optional)</span>
            <input value={form.orderId} onChange={e => setForm({ ...form, orderId: (e.target as HTMLInputElement).value })} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="e.g. 404-1234567-8901234" />
          </label>
        </div>

        <label className="space-y-1 text-sm">
          <span className="text-gray-700">Subject (optional)</span>
          <input value={form.subject} onChange={e => setForm({ ...form, subject: (e.target as HTMLInputElement).value })} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="Question about care instructions" />
        </label>

        <label className="space-y-1 text-sm">
          <span className="text-gray-700">Message <span className="text-red-600" aria-hidden>*</span></span>
          <textarea required rows={6} value={form.message} onChange={e => setForm({ ...form, message: (e.target as HTMLTextAreaElement).value })} onBlur={() => setTouched(t => ({ ...t, message: true }))} aria-invalid={touched.message && !form.message.trim()} className="w-full resize-y rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="Tell us a bit more about how we can help…" />
          {touched.message && !form.message.trim() && (<p className="text-xs text-red-600 mt-1">Please write a short message.</p>)}
        </label>

        {!sent && (
          <div className="flex items-center justify-end gap-3">
            <a href={buildMailto(form)} onClick={(e) => { if (!canSubmit) { e.preventDefault(); setTouched({ name: true, email: true, phone: true, message: true }); setError('Please fill all required fields with valid details before sending.') } }} className="hidden sm:inline-flex items-center gap-2 rounded border border-brand text-brand px-4 py-2 text-sm hover:bg-gray-50">Open in mail app</a>
            <button type="submit" className={`inline-flex items-center gap-2 rounded border border-brand bg-white px-5 py-2 text-sm font-medium text-black transition-colors duration-200 hover:bg-brand hover:text-white`}>Send message</button>
          </div>
        )}
        {status && (
          <p className="mt-3 text-xs text-gray-600">{status}</p>
        )}
      </form>

      <aside className="space-y-4">
        <div className="border bg-white px-5 py-5 shadow-sm">
          <div className="text-sm font-semibold text-gray-900">Reach us directly</div>
          <div className="mt-2 grid grid-cols-3 items-start gap-6">
            {/* Phone */}
            <a href="tel:+918890799893" className="flex flex-col items-center justify-center hover:opacity-90" aria-label="Call us">
              <img src="/assets/brand/icons/phone.svg" alt="Phone" className="h-7 w-7" loading="lazy" decoding="async" width="28" height="28" />
              <span className="mt-1 text-[11px] text-gray-600">+91 88907 99893</span>
            </a>
            {/* WhatsApp (same number; label only) */}
            <a href="https://wa.me/918890799893" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center hover:opacity-90" aria-label="WhatsApp">
              <img src="/assets/brand/icons/whatsapp.svg" alt="WhatsApp" className="h-8 w-8" loading="lazy" decoding="async" width="32" height="32" />
              <span className="mt-1 text-[11px] text-gray-600">WhatsApp</span>
            </a>
            {/* Email */}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="flex flex-col items-center justify-center hover:opacity-90" aria-label="Email us">
              <Mail size={28} className="text-[#EA4335]" />
              <span className="mt-1 text-[11px] text-gray-600">{SUPPORT_EMAIL}</span>
            </a>
          </div>
        </div>

        <div className="border bg-white px-5 py-5 shadow-sm">
          <div className="text-sm font-semibold text-gray-900">Social</div>
          <div className="mt-2 grid grid-cols-3 items-start gap-6">
            <a href="https://www.instagram.com/saffronleo/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex flex-col items-center justify-center hover:opacity-90">
              <img src="/assets/brand/icons/instagram.svg" alt="Instagram" className="h-7 w-7" loading="lazy" decoding="async" width="28" height="28" />
              <span className="mt-1 text-[11px] text-gray-600">@saffronleo</span>
            </a>
            <a href="https://www.facebook.com/saffronleoofficial/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex flex-col items-center justify-center hover:opacity-90">
              <img src="/assets/brand/icons/facebook.svg" alt="Facebook" className="h-8 w-8" loading="lazy" decoding="async" width="32" height="32" />
              <span className="mt-1 text-[11px] text-gray-600">@saffronleoofficial</span>
            </a>
            <a href="https://www.threads.net/@saffronleo" target="_blank" rel="noopener noreferrer" aria-label="Threads" className="flex flex-col items-center justify-center hover:opacity-90">
              <img src="/assets/brand/icons/threads.svg" alt="Threads" className="h-[1.8rem] w-[1.8rem]" loading="lazy" decoding="async" width="29" height="29" />
              <span className="mt-1 text-[11px] text-gray-600">@saffronleo</span>
            </a>
            <a href="https://linkedin.com/company/saffronleoofficial" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="flex flex-col items-center justify-center hover:opacity-90">
              <img src="/assets/brand/icons/linkedin.svg" alt="LinkedIn" className="h-7 w-7" loading="lazy" decoding="async" width="28" height="28" />
              <span className="mt-1 text-[11px] text-gray-600">@saffronleoofficial</span>
            </a>
            <a href="https://www.youtube.com/@Saffronleo" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="flex flex-col items-center justify-center hover:opacity-90">
              <img src="/assets/brand/icons/youtube.svg" alt="YouTube" className="h-7 w-7" loading="lazy" decoding="async" width="28" height="28" />
              <span className="mt-1 text-[11px] text-gray-600">@Saffronleo</span>
            </a>
          </div>
        </div>

        <div className="border bg-white px-5 py-5 shadow-sm">
          <div className="text-sm font-semibold text-gray-900">Orders and returns</div>
          <p className="mt-2 text-sm text-gray-600">If you purchased on Amazon, manage returns and delivery updates from your Amazon account.</p>
          <a href="https://www.amazon.in/gp/css/order-history" target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded border px-4 py-2 text-sm hover:bg-gray-50">
            Go to Amazon orders <ExternalLink size={14} />
          </a>
        </div>
      </aside>
    </section>
  )
}
